import React, { useState } from "react";
import { ProductInterface, QuizMetaEndState } from ".";

import { QuizEmailModal } from "./QuizEmailModal";
import clsx from "clsx";
import QuizResultProduct from "./QuizResultProduct";

export interface QuizResultsProps {
  products: ProductInterface[];
  endState: QuizMetaEndState | null;
  isVegan: boolean;
  isThirdPerson: boolean;
  name: string;
}

const QuizResults: React.FC<QuizResultsProps> = ({
  products,
  isVegan,
  isThirdPerson,
  name,
  endState,
}) => {
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);

  let title = `${name}, here's your BraveFace match.`;

  if (endState) {
    title = `${name}, ${endState.title}`;
  }

  const matchingProducts = products.filter((product) =>
    isVegan ? product.tags.includes("quiz:Vegan") : true,
  );

  const showProducts = matchingProducts.length > 0;
  const showEndState = endState && !showProducts;

  return (
    <div id="quiz-results">
      <QuizEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={async (payload) => {
          await fetch("https://douglas.akqa.nz/api/send", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: payload.email,
              brand: "braveface",
              emailOptIn: payload.emailOptIn,
              firstName: payload.firstName,
              lastName: payload.lastName,
              title: "",
              intro: "",
              collections: [],
            }),
          });
        }}
      />

      {showProducts && (
        <>
          <div
            className={clsx(
              "quiz-result__state",
              "quiz-result__state--white",
              "quiz-active-state",
              {
                "quiz-result__state--multiple": products.length > 1,
              },
            )}
          >
            <div className="quiz-result__state__wrapper">
              <div className="quiz-result__state__content">
                <h1 className="quiz-result__state__title">{title}</h1>{" "}
                <p className="quiz-result__state__desc">
                  It’s time to be brave. Take a look at what science-backed
                  supplement will help you be your best.
                </p>
              </div>{" "}
              <div className="quiz-result__state__products">
                {matchingProducts.map((product) => (
                  <QuizResultProduct key={product.id} product={product} />
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showEndState && (
        <div className="quiz-result__state quiz-result__state--empty quiz-result__state--orange">
          <div className="quiz-result__state__wrapper">
            <div className="quiz-result__state__content">
              <h2 className="h2">
                {isThirdPerson ? endState.titleThirdPerson : endState.title}
              </h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: isThirdPerson
                    ? (endState.contentThirdPerson ?? "")
                    : (endState.content ?? ""),
                }}
              />
            </div>

            <div className="quiz-result__state__image">
              <img
                src="/assets/Footerlink_pages_contact_3_1000x.webp"
                alt="No Vegans"
                width="1000"
                height="1061"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      {!showEndState && !showProducts && (
        <>
          {isVegan ? (
            <div className="quiz-result__state quiz-result__state--empty quiz-result__state--orange">
              <div className="quiz-result__state__wrapper">
                <div className="quiz-result__state__content">
                  <div id="no-vegans">
                    <h2 className="h2">
                      We were unable to match a vegan product to your answers.
                    </h2>
                    <p>
                      All of our products are suitable for vegetarians and
                      HeadRest and LiveCalm are fully vegan. However, CoolHead
                      is not suitable for Vegans as it contains Mānuka honey. We
                      may still be able to help so please{" "}
                      <a href="/pages/contact-us">contact us</a> to chat about
                      what you need.
                    </p>
                  </div>
                </div>

                <div className="quiz-result__state__image">
                  <img
                    src="/assets/Footerlink_pages_contact_3_1000x.webp"
                    alt="No Vegans"
                    width="1000"
                    height="1061"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="quiz-result__state quiz-result__state--empty quiz-result__state--orange">
              <div className="quiz-result__state__wrapper">
                <div className="quiz-result__state__content">
                  <h2 className="h2">
                    We were unable to match a product to your answers.
                  </h2>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="quiz-next-steps">
        <div className="quiz-next-steps__inner">
          <h2>Next steps</h2>
          <ul>
            <li>
              <div className="tile">
                <a
                  onClick={() => setShowEmailModal(true)}
                  className="email-quiz-results"
                >
                  Email your results
                </a>
                <p>
                  Want to know more about your results or a product? Get in
                  touch.
                </p>
              </div>
            </li>
            <li>
              <div className="tile">
                <a href="/pages/contact-us">Contact us</a>
                <p>
                  Want to know more about your results or a product? Get in
                  touch.
                </p>
              </div>
            </li>
            <li>
              <div className="tile">
                <a href="/pages/stockists">Find a stockist</a>
                <p>Find out where to buy BraveFace products in-store.</p>
              </div>
            </li>

            <li>
              <div className="tile">
                <small>
                  All of our products are suitable for vegetarians, but not all
                  are suitable for vegans.
                </small>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="quiz-retake">
        <a href="/pages/quiz?clear=1&start=1" className="btn btn--primary">
          <span className="button__el">Retake the quiz</span>
        </a>
      </div>
    </div>
  );
};

export { QuizResults };
